// @ts-nocheck
function parseTypes(value) {
  return String(value || '')
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);
}

const filterLabels = {
  all: 'All',
  blackout: 'Blackout',
  'light-filtering': 'Light Filtering',
  solar: 'Solar',
  'semi-blackout': 'Semi-Blackout',
  'room-darkening': 'Room Darkening',
  sheer: 'Sheer',
  'semi-sheer': 'Semi-Sheer',
  'dual-blackout': 'Dual Blackout',
  'classification-pending': 'Zebra shades',
};

const CATEGORY_NOUNS = {
  'roller-shades': 'Roller Shade Fabrics',
  'zebra-shades': 'Zebra Shade Fabrics',
  'cellular-shades': 'Cellular Shade Fabrics',
  'shangri-la-sheer-shades': 'Sheer Shade Fabrics',
};

function categoryNounFor(categoryKey) {
  return CATEGORY_NOUNS[categoryKey] || 'Shade Fabrics';
}

function shouldUseDynamicCopy(catalogVersion, categoryKey, isLocked) {
  return catalogVersion === 'catalog-v2' || (categoryKey === 'roller-shades' && isLocked);
}

function normalizeFilter(value, available) {
  const candidate = String(value || 'all');
  return candidate === 'all' || available.includes(candidate) ? candidate : 'all';
}

function parseCounts(value) {
  return String(value || '').split(';').reduce((counts, item) => {
    const [key, rawCount] = item.split(':');
    const count = Number(rawCount);
    if (key && Number.isFinite(count)) counts[key] = count;
    return counts;
  }, {});
}

function withSelection(href, parameter, value) {
  const url = new URL(href, 'https://ndr.invalid');
  if (url.pathname.includes('/pages/')) return href;
  if (!value || value === 'all') url.searchParams.delete(parameter);
  else url.searchParams.set(parameter, value);
  const lockParameter = `lock_${parameter}`;
  if (value && value !== 'all') url.searchParams.set(lockParameter, value);
  else url.searchParams.delete(lockParameter);
  return `${url.pathname}${url.search}${url.hash}`;
}

function shouldLockFilter(sectionLock, params, parameter) {
  return sectionLock === 'true' || Boolean(params.get(`lock_${parameter}`));
}

function updateCardImage(card, selected) {
  const image = card.querySelector('[data-family-image]');
  if (!image) return;
  const type = selected === 'all' ? card.dataset.defaultType : selected;
  const source = image.getAttribute(`data-image-${type}`);
  if (source && image.getAttribute('src') !== source) image.src = source;
}

function updateCardDetails(card, selected) {
  updateCardImage(card, selected);
  const count = selected === 'all'
    ? Number(card.dataset.variantCount || 0)
    : Number(parseCounts(card.dataset.familyCounts)[selected] || 0);
  const uniqueCount = selected === 'all'
    ? card.querySelectorAll('[data-color-types]').length
    : Number(parseCounts(card.dataset.familyUniqueCounts)[selected] || 0);
  const countNode = card.querySelector('[data-family-color-count]');
  if (countNode) {
    const noun = count === uniqueCount ? (count === 1 ? 'color' : 'colors') : 'fabric options';
    countNode.textContent = `${count} ${noun}`;
  }
  const typeLabel = card.querySelector('[data-family-type-label]');
  if (typeLabel) {
    const types = parseTypes(card.dataset.familyTypes);
    typeLabel.textContent = selected === 'all'
      ? types.map((type) => filterLabels[type] || type).join(' · ')
      : (filterLabels[selected] || selected);
  }
  card.querySelectorAll('[data-color-types]').forEach((chip) => {
    chip.hidden = selected !== 'all' && !parseTypes(chip.dataset.colorTypes).includes(selected);
  });
}

function matchesFilters(types, selectedType, subdivision, selectedSubdivision) {
  const typeMatch = selectedType === 'all' || types.includes(selectedType);
  const subdivisionMatch = selectedSubdivision === 'all' || subdivision === selectedSubdivision;
  return typeMatch && subdivisionMatch;
}

function countVisibleCollections(cards) {
  return cards.reduce((sum, card) => {
    if (card.hidden) return sum;
    const value = Number.parseInt(card.dataset.selectableCount ?? card.dataset.collectionCount ?? '1', 10);
    return sum + (Number.isFinite(value) && value >= 0 ? value : 1);
  }, 0);
}

function countVisibleCards(cards) {
  return cards.reduce((count, card) => count + (card.hidden ? 0 : 1), 0);
}

function normalizeSubdivision(requested, available) {
  return requested && available.includes(requested) ? requested : 'all';
}

function swatchPreviewUrl(src, width) {
  const value = String(src || '');
  if (!value) return '';
  if (/([?&])width=\d+/.test(value)) return value.replace(/([?&])width=\d+/, `$1width=${width}`);
  return `${value}${value.includes('?') ? '&' : '?'}width=${width}`;
}

function swatchPreviewLeft(dotCenter, panelWidth, containerWidth) {
  const max = Math.max(0, containerWidth - panelWidth);
  return Math.round(Math.min(max, Math.max(0, dotCenter - panelWidth / 2)));
}

// Color dots on collection cards: hover (mouse), keyboard focus or tap shows a larger fabric close-up and the color name.
// Dots load a small copy of the swatch; the close-up image is requested only when a dot is opened.
function initSwatchPreviews(root) {
  const groups = Array.from(root.querySelectorAll('.ndr-family-card__swatches'));
  if (!groups.length) return;
  let open = null;
  let lastPointer = '';

  const focusVisible = (element) => {
    try {
      return element.matches(':focus-visible');
    } catch (error) {
      return lastPointer === '';
    }
  };

  const panelFor = (group) => {
    let panel = group.querySelector('.ndr-swatch-preview');
    if (panel) return panel;
    panel = document.createElement('div');
    panel.className = 'ndr-swatch-preview';
    panel.setAttribute('aria-hidden', 'true');
    const image = document.createElement('img');
    image.alt = '';
    image.width = 208;
    image.height = 139;
    image.decoding = 'async';
    const name = document.createElement('span');
    panel.append(image, name);
    group.appendChild(panel);
    return panel;
  };

  const close = () => {
    if (!open) return;
    open.panel.classList.remove('is-open');
    open.dot.classList.remove('is-previewed');
    if (open.card) open.card.classList.remove('is-swatch-previewing');
    open = null;
  };

  const show = (dot, pinned) => {
    const group = dot.closest('.ndr-family-card__swatches');
    if (!group) return;
    if (open && open.dot !== dot) close();
    const panel = panelFor(group);
    const thumb = dot.querySelector('img');
    const image = panel.querySelector('img');
    const url = swatchPreviewUrl(thumb ? thumb.getAttribute('src') : '', 416);
    if (url && image.getAttribute('src') !== url) image.setAttribute('src', url);
    panel.querySelector('span').textContent = dot.getAttribute('aria-label') || '';
    panel.style.top = `${dot.offsetTop}px`;
    panel.style.left = `${swatchPreviewLeft(dot.offsetLeft + dot.offsetWidth / 2, panel.offsetWidth, group.clientWidth)}px`;
    panel.classList.add('is-open');
    dot.classList.add('is-previewed');
    const card = dot.closest('.ndr-family-card');
    if (card) card.classList.add('is-swatch-previewing');
    open = { group, dot, panel, card, pinned: Boolean(pinned) };
  };

  groups.forEach((group) => {
    const dots = Array.from(group.querySelectorAll('[data-swatch]'));
    if (!dots.length) return;
    group.setAttribute('role', 'toolbar');
    dots.forEach((dot, index) => dot.setAttribute('tabindex', index === 0 ? '0' : '-1'));

    group.addEventListener('pointerdown', (event) => {
      lastPointer = event.pointerType || '';
    });

    group.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse') return;
      const dot = event.target.closest('[data-swatch]');
      if (dot) show(dot, false);
    });

    group.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || !open || open.group !== group || open.pinned) return;
      const active = document.activeElement;
      if (active && group.contains(active) && focusVisible(active)) return;
      close();
    });

    group.addEventListener('focusin', (event) => {
      const dot = event.target.closest('[data-swatch]');
      if (!dot) return;
      dots.forEach((item) => item.setAttribute('tabindex', item === dot ? '0' : '-1'));
      if (focusVisible(dot)) show(dot, false);
    });

    group.addEventListener('focusout', (event) => {
      if (group.contains(event.relatedTarget)) return;
      if (open && open.group === group && !open.pinned) close();
    });

    group.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-swatch]');
      if (!dot) return;
      if (event.detail === 0 || lastPointer === 'mouse') {
        show(dot, false);
        return;
      }
      if (open && open.dot === dot) close();
      else show(dot, true);
    });

    group.addEventListener('keydown', (event) => {
      const index = dots.indexOf(event.target);
      if (index < 0) return;
      let next = -1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % dots.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + dots.length) % dots.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = dots.length - 1;
      if (next < 0) return;
      event.preventDefault();
      dots[next].focus();
    });
  });

  document.addEventListener('pointerdown', (event) => {
    if (open && !open.group.contains(event.target)) close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && open) close();
  });

  window.addEventListener('resize', () => {
    if (open) show(open.dot, open.pinned);
  });
}

(function () {
  if (typeof document === 'undefined') return;

  const TYPE_LABELS = filterLabels;

  const LEADS = {
    blackout: 'Compare blackout fabric collections first, then choose the exact color, cassette finish, and operating method inside the configurator.',
    'light-filtering': 'Compare light-filtering fabric collections first, then choose the exact color, cassette finish, and operating method inside the configurator.',
    solar: 'Choose an openness level, then compare solar fabrics and colors.',
    'semi-blackout': 'Compare semi-blackout Zebra collections by visual character, then choose the exact palette and color.',
  };

  function normalizePrimary(requested, available, fallback) {
    if (requested && available.includes(requested)) return requested;
    if (fallback && available.includes(fallback)) return fallback;
    return available[0] || 'all';
  }

  function setUrlState(parameter, selectedType, selectedSubdivision, mode, selectedLevel) {
    const url = new URL(window.location.href);
    // Collection level (Essentials / Signature / Premium). Undefined means this grid has no level filter.
    if (selectedLevel !== undefined) {
      if (selectedLevel && selectedLevel !== 'all') url.searchParams.set('level', selectedLevel);
      else url.searchParams.delete('level');
    }
    if (selectedType && selectedType !== 'all') url.searchParams.set(parameter, selectedType);
    else url.searchParams.delete(parameter);
    if (selectedSubdivision && selectedSubdivision !== 'all') {
      url.searchParams.set('subdivision', selectedSubdivision);
    } else {
      url.searchParams.delete('subdivision');
    }
    window.history[mode === 'push' ? 'pushState' : 'replaceState']({}, '', url);
  }

  function init(section) {
    if (section.dataset.familyGridReady === 'true') return;
    section.dataset.familyGridReady = 'true';

    const filterGroup = section.querySelector('[data-filter-group]');
    const buttons = Array.from(section.querySelectorAll('[data-family-filter]'));
    const cards = Array.from(section.querySelectorAll('[data-family-card]'));
    const links = Array.from(section.querySelectorAll('[data-family-link]'));
    const count = section.querySelector('[data-family-count]');
    const empty = section.querySelector('[data-family-empty]');
    const heading = section.querySelector('[data-family-grid-heading]');
    const lead = section.querySelector('[data-family-grid-lead]');
    const subdivisionPanel = section.querySelector('[data-subdivision-panel]');
    const subdivisionGroup = section.querySelector('[data-subdivision-group]');
    const subdivisionAxisLabel = section.querySelector('[data-subdivision-axis-label]');
    const summary = section.querySelector('[data-filter-summary]');
    const summaryText = section.querySelector('[data-filter-summary-text]');
    const summaryChange = section.querySelector('[data-filter-summary-change]');
    const levelButtons = Array.from(section.querySelectorAll('[data-family-level-filter]'));
    const levelDescription = section.querySelector('[data-family-level-description]');
    const levelAvailable = levelButtons.map((button) => button.dataset.familyLevelFilter).filter((value) => value && value !== 'all');
    const levelLabels = new Map(levelButtons.map((button) => [button.dataset.familyLevelFilter, button.dataset.levelLabel || button.textContent.trim()]));
    const parameter = section.dataset.filterParam || 'light_control';
    const lockParameter = `lock_${parameter}`;
    const catalogVersion = section.dataset.catalogVersion || 'v1';
    const categoryKey = section.dataset.categoryKey || '';
    const defaultFilter = section.dataset.defaultFilter || 'all';
    const available = buttons.map((button) => button.dataset.familyFilter).filter(Boolean);
    const originalHeading = heading ? heading.textContent : '';
    const originalLead = lead ? lead.textContent : '';
    let selectedType = defaultFilter;
    let selectedSubdivision = 'all';
    let subdivisionOptions = [];
    let subdivisionLabelById = new Map();
    let isLocked = false;
    let selectedLevel = 'all';

    if (!cards.length) return;

    function levelMatches(card) {
      return !levelButtons.length || selectedLevel === 'all' || card.dataset.familyLevel === selectedLevel;
    }

    function saveUrl(mode) {
      setUrlState(parameter, selectedType, selectedSubdivision, mode, levelButtons.length ? selectedLevel : undefined);
    }

    function syncSummaryVisibility() {
      if (!summary || !subdivisionPanel || subdivisionPanel.hidden) {
        if (summary) summary.classList.remove('is-visible');
        return;
      }
      const panelBottom = subdivisionPanel.getBoundingClientRect().bottom;
      summary.classList.toggle('is-visible', panelBottom <= 72);
    }

    function resetSummaryObserver() {
      if (summary) summary.classList.remove('is-visible');
      window.requestAnimationFrame(syncSummaryVisibility);
    }

    window.addEventListener('scroll', syncSummaryVisibility, { passive: true });
    window.addEventListener('resize', syncSummaryVisibility);

    function primaryCards() {
      return cards.filter((card) => {
        const types = parseTypes(card.dataset.familyTypes || card.dataset.defaultType);
        return (selectedType === 'all' || types.includes(selectedType)) && levelMatches(card);
      });
    }

    function buildSubdivisionOptions() {
      // All fabrics page: looks and openness belong to one shade style, so hide them while All is picked.
      if (catalogVersion !== 'catalog-v2' || !subdivisionPanel || !subdivisionGroup || selectedType === 'all') {
        subdivisionOptions = [];
        subdivisionLabelById = new Map();
        if (subdivisionPanel) {
          subdivisionPanel.hidden = true;
          resetSummaryObserver();
        }
        return;
      }

      const applicable = primaryCards();
      const models = new Map();
      let axisLabel = '';
      let allCount = 0;
      let allDocumentedCount = 0;
      let representativeImage = '';

      applicable.forEach((card) => {
        const rawWeight = Number.parseInt(card.dataset.selectableCount ?? card.dataset.collectionCount ?? '1', 10);
        const weight = Number.isFinite(rawWeight) && rawWeight >= 0 ? rawWeight : 1;
        const rawDocumented = Number.parseInt(card.dataset.collectionCount ?? '1', 10);
        const documentedWeight = Number.isFinite(rawDocumented) && rawDocumented >= 0 ? rawDocumented : 1;
        allCount += weight;
        allDocumentedCount += documentedWeight;
        const cardImage = card.querySelector('.ndr-family-card__fabric-media img');
        if (!representativeImage && cardImage) representativeImage = cardImage.currentSrc || cardImage.src;
        const id = card.dataset.familySubdivision;
        if (!id) return;
        axisLabel = axisLabel || card.dataset.subdivisionAxisLabel || 'Shop by look';
        const current = models.get(id) || {
          id,
          label: card.dataset.subdivisionLabel || id,
          order: Number.parseInt(card.dataset.subdivisionOrder || '999', 10),
          count: 0,
          documentedCount: 0,
          image: cardImage ? cardImage.currentSrc || cardImage.src : '',
        };
        current.count += weight;
        current.documentedCount += documentedWeight;
        models.set(id, current);
      });

      const ordered = Array.from(models.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
      subdivisionOptions = ordered.map((item) => item.id);
      subdivisionLabelById = new Map(ordered.map((item) => [item.id, item.label]));
      selectedSubdivision = normalizeSubdivision(selectedSubdivision, subdivisionOptions);
      subdivisionGroup.replaceChildren();

      if (!ordered.length) {
        subdivisionPanel.hidden = true;
        resetSummaryObserver();
        return;
      }

      const allLabel = axisLabel.toLowerCase().includes('openness') ? 'All openness levels' : 'All looks';
      const modelsWithAll = [{ id: 'all', label: allLabel, count: allCount, documentedCount: allDocumentedCount, image: representativeImage }, ...ordered];
      modelsWithAll.forEach((model) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ndr-family-grid__subdivision-option';
        button.dataset.familySubdivisionFilter = model.id;
        button.setAttribute('aria-pressed', model.id === selectedSubdivision ? 'true' : 'false');
        if (model.id === selectedSubdivision) button.classList.add('is-active');
        if (model.image) {
          const image = document.createElement('img');
          image.src = model.image;
          image.alt = '';
          image.width = 48;
          image.height = 48;
          image.loading = 'lazy';
          button.appendChild(image);
        }
        const text = document.createElement('span');
        const strong = document.createElement('strong');
        strong.textContent = model.label;
        const small = document.createElement('small');
        const availabilityPrefix = model.documentedCount > model.count ? 'available ' : '';
        small.textContent = `${model.count} ${availabilityPrefix}${model.count === 1 ? 'collection' : 'collections'}`;
        text.append(strong, small);
        button.appendChild(text);
        button.addEventListener('click', () => {
          if (selectedSubdivision === model.id) return;
          selectedSubdivision = model.id;
          saveUrl('push');
          apply();
        });
        subdivisionGroup.appendChild(button);
      });

      subdivisionAxisLabel.textContent = axisLabel;
      subdivisionGroup.setAttribute('aria-label', axisLabel);
      subdivisionPanel.hidden = false;
      window.requestAnimationFrame(resetSummaryObserver);
    }

    function updateButtonStates() {
      buttons.forEach((button) => {
        const active = button.dataset.familyFilter === selectedType;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (isLocked && catalogVersion === 'catalog-v2') {
          button.hidden = !active;
          button.disabled = active;
          button.setAttribute('aria-disabled', active ? 'true' : 'false');
        } else {
          button.hidden = false;
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
      });
      if (filterGroup) {
        if (isLocked && catalogVersion !== 'catalog-v2') filterGroup.hidden = true;
        else filterGroup.hidden = false;
        filterGroup.classList.toggle('is-locked', isLocked);
      }
      levelButtons.forEach((button) => {
        const active = button.dataset.familyLevelFilter === selectedLevel;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (subdivisionGroup) {
        subdivisionGroup.querySelectorAll('[data-family-subdivision-filter]').forEach((button) => {
          const active = button.dataset.familySubdivisionFilter === selectedSubdivision;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }
    }

    function updateCopy(visibleCollections, visibleDocumentedCollections) {
      const selectedLabel = TYPE_LABELS[selectedType] || selectedType;
      const availabilityPrefix = visibleDocumentedCollections > visibleCollections ? 'available ' : '';
      const collectionLabel = `${visibleCollections} ${availabilityPrefix}${visibleCollections === 1 ? 'collection' : 'collections'}`;
      const useDynamicCopy = shouldUseDynamicCopy(catalogVersion, categoryKey, isLocked);
      if (heading) {
        heading.textContent = useDynamicCopy && selectedType !== 'all'
          ? `${selectedLabel} ${categoryNounFor(categoryKey)}`
          : originalHeading;
      }
      if (lead) {
        if (!useDynamicCopy || selectedType === 'all') lead.textContent = originalLead;
        else if (catalogVersion !== 'catalog-v2' && categoryKey === 'roller-shades') {
          lead.textContent = `Choose a ${selectedLabel} fabric collection, then customize your shade.`;
        } else lead.textContent = LEADS[selectedType] || originalLead;
      }
      if (count) count.textContent = collectionLabel;
      const subdivisionLabel = selectedSubdivision === 'all'
        ? ((subdivisionAxisLabel && subdivisionAxisLabel.textContent.toLowerCase().includes('openness')) ? 'All openness levels' : 'All looks')
        : (subdivisionLabelById.get(selectedSubdivision) || selectedSubdivision);
      const levelLabel = selectedLevel === 'all' ? '' : (levelLabels.get(selectedLevel) || selectedLevel);
      if (summaryText) {
        summaryText.textContent = [selectedLabel, levelLabel, subdivisionLabel, collectionLabel].filter(Boolean).join(' · ');
      }
      if (summary) summary.hidden = !(summaryText && subdivisionOptions.length);
      if (levelDescription) {
        const activeLevel = levelButtons.find((button) => button.dataset.familyLevelFilter === selectedLevel);
        const copy = selectedLevel === 'all' || !activeLevel ? '' : (activeLevel.dataset.levelDescription || '');
        levelDescription.textContent = copy;
        levelDescription.hidden = !copy;
      }
    }

    function apply() {
      cards.forEach((card) => {
        const types = parseTypes(card.dataset.familyTypes || card.dataset.defaultType);
        card.hidden = !matchesFilters(types, selectedType, card.dataset.familySubdivision || '', selectedSubdivision) || !levelMatches(card);
        updateCardDetails(card, selectedType);
        const linkValue = selectedType === 'all' ? card.dataset.defaultType : selectedType;
        card.querySelectorAll('[data-family-link]').forEach((link) => {
          const base = link.dataset.baseUrl || link.getAttribute('href') || '';
          link.setAttribute('href', withSelection(base, parameter, linkValue));
        });
      });
      const visibleCollections = countVisibleCollections(cards);
      const visibleDocumentedCollections = cards.reduce((sum, card) => {
        if (card.hidden) return sum;
        const value = Number.parseInt(card.dataset.collectionCount ?? '1', 10);
        return sum + (Number.isFinite(value) && value >= 0 ? value : 1);
      }, 0);
      updateButtonStates();
      updateCopy(visibleCollections, visibleDocumentedCollections);
      if (empty) empty.hidden = countVisibleCards(cards) !== 0;
    }

    function loadFromUrl(replaceInvalid) {
      const params = new URLSearchParams(window.location.search);
      const requested = params.get(parameter);
      const locked = params.get(lockParameter);
      isLocked = shouldLockFilter(section.dataset.lockFilter, params, parameter);
      selectedType = normalizePrimary(isLocked ? (locked || requested) : requested, available, defaultFilter);
      selectedSubdivision = params.get('subdivision') || 'all';
      const requestedLevel = params.get('level');
      selectedLevel = levelButtons.length && levelAvailable.includes(requestedLevel) ? requestedLevel : 'all';
      const levelWasInvalid = Boolean(levelButtons.length) && requestedLevel !== null && requestedLevel !== selectedLevel;
      buildSubdivisionOptions();
      const normalizedSubdivision = normalizeSubdivision(selectedSubdivision, subdivisionOptions);
      const wasInvalid = selectedSubdivision !== 'all' && normalizedSubdivision !== selectedSubdivision;
      selectedSubdivision = normalizedSubdivision;
      if (replaceInvalid && (requested !== selectedType || wasInvalid || levelWasInvalid)) {
        saveUrl('replace');
      }
      apply();
    }

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        if (isLocked || button.dataset.familyFilter === selectedType) return;
        selectedType = button.dataset.familyFilter || defaultFilter;
        selectedSubdivision = 'all';
        buildSubdivisionOptions();
        saveUrl('push');
        apply();
      });
    });

    levelButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.familyLevelFilter || 'all';
        if (value === selectedLevel) return;
        selectedLevel = value;
        buildSubdivisionOptions();
        saveUrl('push');
        apply();
      });
    });

    if (summaryChange && subdivisionPanel) {
      summaryChange.addEventListener('click', () => {
        subdivisionPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const active = subdivisionPanel.querySelector('.ndr-family-grid__subdivision-option.is-active');
        if (active) active.focus({ preventScroll: true });
      });
    }

    initSwatchPreviews(section);
    window.addEventListener('popstate', () => loadFromUrl(false));
    loadFromUrl(true);
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-no-drill-family-grid]').forEach(init);
    // Top Picks row (Albert, Oct 17 2026): same color dots and preview as the collection cards.
    (root || document).querySelectorAll('[data-ndr-swatch-previews]').forEach((row) => {
      if (row.dataset.swatchPreviewsReady) return;
      row.dataset.swatchPreviewsReady = 'true';
      initSwatchPreviews(row);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();

if (typeof module !== 'undefined') {
  module.exports = {
    parseTypes,
    parseCounts,
    normalizeFilter,
    withSelection,
    shouldLockFilter,
    matchesFilters,
    countVisibleCollections,
    countVisibleCards,
    categoryNounFor,
    shouldUseDynamicCopy,
    normalizeSubdivision,
    swatchPreviewUrl,
    swatchPreviewLeft,
  };
}
